import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Terminal,
  RefreshCw,
  Key,
  Copy,
  Eye,
  EyeOff,
  Check,
  X,
  CreditCard,
  DollarSign,
  Percent,
  Activity,
  TestTube,
  AlertCircle,
  UserCircle,
  History,
  ChevronLeft,
  ChevronRight,
  Bell,
  Send,
  MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const TEST_CARDS = [
  { type: 'success', number: '4111 1111 1111 1111', expiry: '12/25', cvv: '123', description: 'Успешный платёж', color: 'emerald' },
  { type: 'decline', number: '4000 0000 0000 0002', expiry: '12/25', cvv: '123', description: 'Отклонённый платёж', color: 'red' },
  { type: '3ds', number: '4000 0000 0000 3220', expiry: '12/25', cvv: '123', description: 'Требует 3D Secure', color: 'blue' },
  { type: 'error', number: '4000 0000 0000 0119', expiry: '12/25', cvv: '123', description: 'Ошибка обработки', color: 'amber' },
];

const MerchantCabinetPage = () => {
  const { user } = useAuth();
  const [terminals, setTerminals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [merchantInfo, setMerchantInfo] = useState(null);
  
  // Payments history
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsPagination, setPaymentsPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Sandbox mode
  const [sandboxMode, setSandboxMode] = useState(false);
  const [showTestCards, setShowTestCards] = useState(false);
  
  // API Key dialog
  const [keysDialog, setKeysDialog] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [merchantApiKey, setMerchantApiKey] = useState(null);
  const [loadingApiKey, setLoadingApiKey] = useState(false);

  // Telegram notifications
  const [telegramSettings, setTelegramSettings] = useState({ telegram_chat_id: "", telegram_username: "", notifications_enabled: true });
  const [savingTelegram, setSavingTelegram] = useState(false);
  const [testingTelegram, setTestingTelegram] = useState(false);

  const isImpersonating = !!localStorage.getItem('impersonate_token');
  
  const getMerchantDisplayName = () => {
    if (isImpersonating) {
      try {
        const impersonateData = JSON.parse(localStorage.getItem('impersonate_merchant') || '{}');
        return impersonateData.name || 'Мерчант';
      } catch {
        return 'Мерчант';
      }
    }
    return merchantInfo?.name || user?.name || 'Мерчант';
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [terminalsRes, statsRes, infoRes] = await Promise.all([
        api.get('/cabinet/terminals'),
        api.get('/cabinet/stats'),
        api.get('/cabinet/info').catch(() => ({ data: null })),
      ]);
      
      const terminalsData = terminalsRes.data.terminals || [];
      setTerminals(terminalsData);
      setStats(statsRes.data);
      if (infoRes.data) {
        setMerchantInfo(infoRes.data);
      }
      
      const hasTestTerminal = terminalsData.some(t => t.test_mode);
      setSandboxMode(hasTestTerminal);
    } catch (err) {
      console.error('Error loading cabinet data:', err);
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async (page = 1, status = statusFilter) => {
    try {
      setPaymentsLoading(true);
      const params = { page, page_size: paymentsPagination.pageSize };
      if (status && status !== 'all') {
        params.status = status;
      }
      
      const response = await api.get('/cabinet/payments', { params });
      setPayments(response.data.payments || []);
      setPaymentsPagination({
        ...paymentsPagination,
        page: response.data.page,
        total: response.data.total,
      });
    } catch (err) {
      console.error('Error loading payments:', err);
      toast.error('Ошибка загрузки платежей');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const fetchMerchantApiKey = async () => {
    try {
      setLoadingApiKey(true);
      const response = await api.get('/cabinet/api-key');
      setMerchantApiKey(response.data);
    } catch (err) {
      console.error('Error loading API key:', err);
      toast.error('Ошибка загрузки API ключа');
    } finally {
      setLoadingApiKey(false);
    }
  };

  // Telegram functions
  const fetchTelegramSettings = async () => {
    try {
      const response = await api.get("/cabinet/telegram-settings");
      setTelegramSettings(response.data);
    } catch (err) {
      console.error("Error loading telegram settings:", err);
    }
  };

  const saveTelegramSettings = async () => {
    try {
      setSavingTelegram(true);
      await api.post("/cabinet/telegram-settings", telegramSettings);
      toast.success("Настройки уведомлений сохранены");
    } catch (err) {
      console.error("Error saving telegram settings:", err);
      toast.error("Ошибка сохранения настроек");
    } finally {
      setSavingTelegram(false);
    }
  };

  const sendTestNotification = async () => {
    try {
      setTestingTelegram(true);
      await api.post("/cabinet/telegram-test");
      toast.success("Тестовое уведомление отправлено!");
    } catch (err) {
      console.error("Error sending test notification:", err);
      toast.error(err.response?.data?.detail || "Ошибка отправки уведомления");
    } finally {
      setTestingTelegram(false);
    }
  };

  const openApiKeyDialog = (terminal) => {
    setSelectedTerminal(terminal);
    setShowApiKey(false);
    setKeysDialog(true);
    if (!merchantApiKey) {
      fetchMerchantApiKey();
    }
  };

  useEffect(() => {
    fetchData();
    fetchTelegramSettings();
    fetchPayments();
  }, []);

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    fetchPayments(1, newStatus);
  };

  const openKeysDialog = (terminal) => {
    setSelectedTerminal(terminal);
    setShowApiKey(false);
    setKeysDialog(true);
    if (!merchantApiKey) {
      fetchMerchantApiKey();
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} скопирован`);
    } catch (err) {
      toast.error('Ошибка копирования');
    }
  };

  const formatCurrency = (amount, currency = 'KZT') => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      success: { label: 'Успешно', className: 'bg-emerald-500/20 text-emerald-400' },
      pending: { label: 'В обработке', className: 'bg-amber-500/20 text-amber-400' },
      declined: { label: 'Отклонён', className: 'bg-red-500/20 text-red-400' },
      failed: { label: 'Ошибка', className: 'bg-red-500/20 text-red-400' },
      error: { label: 'Ошибка', className: 'bg-red-500/20 text-red-400' },
      refunded: { label: 'Возврат', className: 'bg-purple-500/20 text-purple-400' },
    };
    const config = statusMap[status] || { label: status, className: 'bg-slate-500/20 text-slate-400' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const handleExitImpersonation = () => {
    localStorage.removeItem('impersonate_token');
    localStorage.removeItem('impersonate_merchant');
    window.close();
  };

  return (
    <div className="space-y-6" data-testid="merchant-cabinet-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Личный кабинет</h1>
          <div className="flex items-center gap-2 mt-1">
            <UserCircle className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 font-medium">{getMerchantDisplayName()}</span>
            {isImpersonating && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-400">
                Режим просмотра
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4">
          {isImpersonating && (
            <Button onClick={handleExitImpersonation} variant="outline" className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20">
              Выйти из просмотра
            </Button>
          )}
          
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-800 rounded-lg border border-slate-700">
            <TestTube className={`w-4 h-4 ${sandboxMode ? 'text-amber-400' : 'text-slate-500'}`} />
            <Label htmlFor="sandbox-mode" className="text-sm text-slate-300 cursor-pointer">Тестовый режим</Label>
            <Switch id="sandbox-mode" checked={sandboxMode} onCheckedChange={(checked) => { setSandboxMode(checked); if (checked) setShowTestCards(true); }} />
          </div>
          
          <Button onClick={fetchData} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Sandbox Banner */}
      {sandboxMode && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <div className="flex items-start gap-3">
            <TestTube className="w-5 h-5 text-amber-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-amber-400 font-medium">Тестовый режим активен</p>
              <p className="text-sm text-amber-400/70 mt-1">Используйте тестовые карты для симуляции платежей.</p>
              <Button variant="outline" size="sm" onClick={() => setShowTestCards(true)} className="mt-3 border-amber-500/50 text-amber-400 hover:bg-amber-500/20">
                <CreditCard className="w-4 h-4 mr-2" />
                Показать тестовые карты
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Всего платежей</p>
                  <p className="text-2xl font-bold text-white">{stats.total_payments?.toLocaleString()}</p>
                </div>
                <Activity className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Успешных</p>
                  <p className="text-2xl font-bold text-emerald-400">{stats.successful_payments?.toLocaleString()}</p>
                </div>
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Отклонённых</p>
                  <p className="text-2xl font-bold text-red-400">{stats.declined_payments?.toLocaleString()}</p>
                </div>
                <X className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Общий оборот</p>
                  {stats.turnover_by_currency && stats.turnover_by_currency.length > 0 ? (
                    <div className="space-y-1">
                      {stats.turnover_by_currency.map((item, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <p className="text-xl font-bold text-white">
                            {formatCurrency(item.amount || 0, item.currency)}
                          </p>
                          <span className="text-xs text-slate-500">
                            ({item.successful_count} платежей)
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xl font-bold text-white">{formatCurrency(stats.total_amount || 0)}</p>
                  )}
                </div>
                <DollarSign className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs: Terminals & Payments */}
      <Tabs defaultValue="terminals" className="w-full">
        <TabsList className="bg-slate-800 border border-slate-700">
          <TabsTrigger value="terminals" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
            <Terminal className="w-4 h-4 mr-2" />
            Терминалы
          </TabsTrigger>
          <TabsTrigger value="payments" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
            <History className="w-4 h-4 mr-2" />
            История платежей
          </TabsTrigger>
        <TabsTrigger value="notifications" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
            <Bell className="w-4 h-4 mr-2" />
            Уведомления
          </TabsTrigger>
        </TabsList>

        {/* Terminals Tab */}
        <TabsContent value="terminals">
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Terminal className="w-5 h-5 text-emerald-500" />
                Мои терминалы
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
                </div>
              ) : terminals.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                  <Terminal className="w-12 h-12 mb-4 opacity-50" />
                  <p>Нет подключенных терминалов</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left p-4 text-sm font-medium text-slate-400">Терминал</th>
                        <th className="text-center p-4 text-sm font-medium text-slate-400">Валюта</th>
                        <th className="text-center p-4 text-sm font-medium text-slate-400">Комиссия</th>
                        <th className="text-center p-4 text-sm font-medium text-slate-400">Лимиты</th>
                        <th className="text-center p-4 text-sm font-medium text-slate-400">Режим</th>
                        <th className="text-center p-4 text-sm font-medium text-slate-400">Статус</th>
                        <th className="text-right p-4 text-sm font-medium text-slate-400">API Key</th>
                      </tr>
                    </thead>
                    <tbody>
                      {terminals.map((terminal) => (
                        <tr key={terminal.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="p-4">
                            <div>
                              <span className="text-white font-medium">{terminal.name}</span>
                              <p className="text-xs text-slate-500">ID: {terminal.id}</p>
                            </div>
                          </td>
                          <td className="p-4 text-center">
                            <span className="px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-400">
                              {terminal.currency}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            {terminal.commissions?.percent ? (
                              <div>
                                <span className="text-white font-medium flex items-center justify-center gap-1">
                                  <Percent className="w-3 h-3" />{terminal.commissions.percent}%
                                </span>
                                {terminal.commissions.min && <p className="text-xs text-slate-500">мин. {terminal.commissions.min}</p>}
                              </div>
                            ) : <span className="text-slate-500">—</span>}
                          </td>
                          <td className="p-4 text-center">
                            {terminal.limits?.payment_min || terminal.limits?.payment_max ? (
                              <div className="text-xs text-slate-300">
                                {terminal.limits.payment_min && <p>от {terminal.limits.payment_min.toLocaleString()}</p>}
                                {terminal.limits.payment_max && <p>до {terminal.limits.payment_max.toLocaleString()}</p>}
                              </div>
                            ) : <span className="text-slate-500">—</span>}
                          </td>
                          <td className="p-4 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${terminal.test_mode ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                              {terminal.test_mode ? 'ТЕСТ' : 'ПРОД'}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${terminal.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>
                              {terminal.is_active ? <><Check className="w-3 h-3" />Активен</> : <><X className="w-3 h-3" />Неактивен</>}
                            </span>
                          </td>
                          <td className="p-4 text-right">
                            <Button variant="outline" size="sm" onClick={() => openKeysDialog(terminal)} className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20">
                              <Key className="w-4 h-4 mr-1" />Получить
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-white">
                <History className="w-5 h-5 text-emerald-500" />
                История платежей
              </CardTitle>
              <div className="flex items-center gap-2">
                <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                  <SelectTrigger className="w-40 bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Статус" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все статусы</SelectItem>
                    <SelectItem value="success">Успешные</SelectItem>
                    <SelectItem value="pending">В обработке</SelectItem>
                    <SelectItem value="declined">Отклонённые</SelectItem>
                    <SelectItem value="refunded">Возвраты</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => fetchPayments(paymentsPagination.page)} className="border-slate-600 text-slate-300">
                  <RefreshCw className={`w-4 h-4 ${paymentsLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {paymentsLoading && payments.length === 0 ? (
                <div className="flex items-center justify-center py-20">
                  <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
                </div>
              ) : payments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                  <History className="w-12 h-12 mb-4 opacity-50" />
                  <p>Нет платежей</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left p-4 text-sm font-medium text-slate-400">ID / Заказ</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">Сумма</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">Карта</th>
                          <th className="text-center p-4 text-sm font-medium text-slate-400">Статус</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-400">Дата</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((payment) => (
                          <tr key={payment.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                            <td className="p-4">
                              <div>
                                <span className="text-white font-mono text-sm">#{payment.id}</span>
                                {payment.order_number && <p className="text-xs text-slate-500">{payment.order_number}</p>}
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="text-white font-medium">{formatCurrency(payment.amount, payment.currency)}</span>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <CreditCard className="w-4 h-4 text-slate-400" />
                                <span className="text-slate-300 font-mono text-sm">{payment.card_mask || '****'}</span>
                                {payment.card_type && <span className="text-xs text-slate-500">{payment.card_type}</span>}
                              </div>
                            </td>
                            <td className="p-4 text-center">
                              {getStatusBadge(payment.status)}
                            </td>
                            <td className="p-4">
                              <span className="text-slate-400 text-sm">{formatDate(payment.created_at)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  {Math.ceil(paymentsPagination.total / paymentsPagination.pageSize) > 1 && (
                    <div className="flex items-center justify-between p-4 border-t border-slate-700">
                      <p className="text-sm text-slate-400">
                        Страница {paymentsPagination.page} из {Math.ceil(paymentsPagination.total / paymentsPagination.pageSize)} ({paymentsPagination.total} платежей)
                      </p>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => fetchPayments(paymentsPagination.page - 1)} disabled={paymentsPagination.page <= 1} className="border-slate-600 text-slate-300">
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => fetchPayments(paymentsPagination.page + 1)} disabled={paymentsPagination.page >= Math.ceil(paymentsPagination.total / paymentsPagination.pageSize)} className="border-slate-600 text-slate-300">
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Bell className="w-5 h-5 text-emerald-500" />
                Настройки уведомлений
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <MessageCircle className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div>
                    <p className="text-blue-400 font-medium">Telegram уведомления</p>
                    <p className="text-sm text-blue-400/70 mt-1">
                      Получайте мгновенные уведомления о платежах в Telegram.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Telegram Chat ID</label>
                  <Input
                    placeholder="Например: 123456789 или @username"
                    value={telegramSettings.telegram_chat_id || ""}
                    onChange={(e) => setTelegramSettings({...telegramSettings, telegram_chat_id: e.target.value})}
                    className="bg-slate-900 border-slate-600 text-white"
                  />
                  <p className="text-xs text-slate-500">
                    Получите Chat ID у бота @userinfobot или используйте username канала (@mychannel)
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Telegram Username (для справки)</label>
                  <Input
                    placeholder="@yourusername"
                    value={telegramSettings.telegram_username || ""}
                    onChange={(e) => setTelegramSettings({...telegramSettings, telegram_username: e.target.value})}
                    className="bg-slate-900 border-slate-600 text-white"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Получать уведомления</p>
                    <p className="text-sm text-slate-400">Уведомления о статусах платежей и выплат</p>
                  </div>
                  <Switch
                    checked={telegramSettings.notifications_enabled}
                    onCheckedChange={(checked) => setTelegramSettings({...telegramSettings, notifications_enabled: checked})}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  onClick={saveTelegramSettings}
                  disabled={savingTelegram}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {savingTelegram ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Сохранить
                </Button>
                <Button
                  variant="outline"
                  onClick={sendTestNotification}
                  disabled={testingTelegram || !telegramSettings.telegram_chat_id}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  {testingTelegram ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Тестовое уведомление
                </Button>
              </div>

              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-sm text-amber-400">
                  <strong>Инструкция:</strong><br/>
                  1. Найдите нашего бота: @OxyPayNotifyBot<br/>
                  2. Отправьте команду /start<br/>
                  3. Получите свой Chat ID и введите его выше
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Test Cards Dialog */}
      <Dialog open={showTestCards} onOpenChange={setShowTestCards}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-500" />
              Тестовые карты
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5" />
              <p className="text-sm text-amber-400">Используйте эти карты только в тестовом режиме.</p>
            </div>
            <div className="grid gap-3">
              {TEST_CARDS.map((card, index) => (
                <div key={index} className="p-4 rounded-lg border bg-slate-900/50 border-slate-600">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`font-medium text-sm mb-2 ${card.color === 'emerald' ? 'text-emerald-400' : card.color === 'red' ? 'text-red-400' : card.color === 'blue' ? 'text-blue-400' : 'text-amber-400'}`}>
                        {card.description}
                      </p>
                      <div className="space-y-1">
                        <div className="flex items-center gap-3">
                          <span className="text-slate-500 text-xs w-16">Номер:</span>
                          <code className="font-mono text-white">{card.number}</code>
                          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(card.number.replace(/\s/g, ''), 'Номер')} className="h-6 w-6 p-0 text-slate-400 hover:text-white">
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-slate-500 text-xs w-16">Срок:</span>
                          <code className="font-mono text-white">{card.expiry}</code>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-slate-500 text-xs w-16">CVV:</span>
                          <code className="font-mono text-white">{card.cvv}</code>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500">• Для 3D Secure используйте код: 123456</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* API Keys Dialog */}
      <Dialog open={keysDialog} onOpenChange={setKeysDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-500" />
              API интеграция
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Merchant Private Key - главный ключ для API */}
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <label className="text-sm font-medium text-emerald-400">Merchant Private Key (для API)</label>
              </div>
              {loadingApiKey ? (
                <div className="flex items-center gap-2 text-slate-400">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Загрузка...
                </div>
              ) : merchantApiKey ? (
                <>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-3 bg-slate-900 rounded-lg font-mono text-lg text-emerald-400 overflow-x-auto">
                      {showApiKey ? merchantApiKey.api_key : '••••••••••••••••••••••••••••••••'}
                    </code>
                    <Button variant="outline" size="sm" onClick={() => setShowApiKey(!showApiKey)} className="border-emerald-600 text-emerald-300 hover:bg-emerald-500/20">
                      {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(merchantApiKey.api_key, 'API Key')} className="border-emerald-600 text-emerald-300 hover:bg-emerald-500/20">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-emerald-400/70">
                    ✓ Используйте этот ключ в заголовке: <code className="bg-slate-900 px-1 rounded">Authorization: Bearer YOUR_KEY</code>
                  </p>
                </>
              ) : (
                <p className="text-sm text-red-400">Не удалось загрузить ключ</p>
              )}
            </div>

            {/* Terminal API Key - для справки */}
            {selectedTerminal && (
              <div className="p-4 bg-slate-700/30 rounded-lg space-y-3">
                <label className="text-sm text-slate-400">Terminal ID: {selectedTerminal.name}</label>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500">Валюта:</span>
                    <span className="ml-2 text-white">{selectedTerminal.currency}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Режим:</span>
                    <span className={`ml-2 ${selectedTerminal.test_mode ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {selectedTerminal.test_mode ? 'Тестовый' : 'Продакшн'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* API Documentation Link */}
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-400">
                📚 Документация API: <a href="https://docs.oxypay.kz" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-300">docs.oxypay.kz</a>
              </p>
              <p className="text-xs text-blue-400/70 mt-1">
                API URL: <code className="bg-slate-900 px-1 rounded">https://business.oxypay.kz/api/v1</code>
              </p>
            </div>

            <p className="text-xs text-red-400/70">⚠️ Никому не передавайте ваш API ключ!</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MerchantCabinetPage;
